import { useMemo, useState } from 'react';
import { ChevronRight, Search } from 'lucide-react';
import type { MenuNode } from '@/lib/types';
import { cn } from '@/lib/cn';
import { groupIcon, leafIcon, trLower } from './menu';

function filterTree(nodes: MenuNode[], q: string): MenuNode[] {
  if (!q) return nodes;
  const s = trLower(q);
  const out: MenuNode[] = [];
  for (const n of nodes) {
    const kids = n.children ? filterTree(n.children, q) : undefined;
    const self = trLower(n.title).includes(s);
    if (self || (kids && kids.length)) out.push({ ...n, children: kids?.length ? kids : self ? n.children : kids });
  }
  return out;
}

const LS = 'dolphin.menuOpen';

/** Tasarım belirteçleriyle (--side-*) çizilen menü ağacı; kenar menü ve ikon rayı panelinde kullanılır */
export function MenuTree({ nodes, onOpen, activeTag, search = true, topIcons = true }: {
  nodes: MenuNode[]; onOpen: (n: MenuNode) => void; activeTag?: number | null; search?: boolean; topIcons?: boolean;
}) {
  const [q, setQ] = useState('');
  const [openKeys, setOpenKeys] = useState<Set<string>>(() => {
    try { return new Set(JSON.parse(localStorage.getItem(LS) ?? '[]')); } catch { return new Set(); }
  });
  const toggle = (k: string) => setOpenKeys((p) => {
    const n = new Set(p);
    n.has(k) ? n.delete(k) : n.add(k);
    try { localStorage.setItem(LS, JSON.stringify([...n])); } catch { /* */ }
    return n;
  });
  const shown = useMemo(() => filterTree(nodes, q.trim()), [nodes, q]);

  const render = (list: MenuNode[], depth: number) => list.map((n) => {
    const isGroup = !!n.children;
    const open = q ? true : openKeys.has(n.key);
    const active = !isGroup && activeTag != null && n.tag === activeTag;
    const Icon = isGroup ? (depth === 0 ? groupIcon(n) : null) : depth === 0 ? leafIcon(n.kind) : null;
    return (
      <div key={n.key}>
        <button
          className={cn('side-item', depth === 0 && isGroup && 'side-group', active && 'is-active', isGroup && open && 'is-open')}
          style={{ paddingLeft: 10 + depth * 14 + (depth > 0 && !Icon ? 18 : 0) }}
          onClick={() => (isGroup ? toggle(n.key) : onOpen(n))}
          title={n.title}
        >
          {Icon && topIcons && <Icon size={16} className="side-icon" />}
          {!Icon && isGroup && <ChevronRight size={13} className={cn('side-chev -ml-[18px]', open && 'rotate-90')} />}
          <span className="min-w-0 flex-1 truncate">{n.title}</span>
          {isGroup && depth === 0 && <ChevronRight size={14} className={cn('side-chev transition-transform', open && 'rotate-90')} />}
          {n.shortcut && !isGroup && <span className="side-kbd">{n.shortcut}</span>}
        </button>
        {isGroup && open && <div className={cn(depth === 0 && 'side-sub')}>{render(n.children!, depth + 1)}</div>}
      </div>
    );
  });

  return (
    <div className="flex h-full min-h-0 flex-col">
      {search && (
        <div className="relative px-3 pb-2">
          <Search size={14} className="pointer-events-none absolute left-[22px] top-[9px] text-[var(--side-muted)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Menüde ara…" className="side-search" />
        </div>
      )}
      <div className="min-h-0 flex-1 space-y-0.5 overflow-auto px-2 pb-4 scroll-thin">{render(shown, 0)}</div>
    </div>
  );
}
